import React, { FunctionComponent, useEffect, useState, useRef, useCallback } from "react";
import { announcePolite } from "@components/LiveRegion";
import { ConveyApi, ConveySocket, IMessageEntity, IDialectEntity, ITemplateEntity } from "@utils";
import {
  ChatWrapContainer,
  ChatContainer,
  ChatText,
  ChatInputContainer,
  ChatInput,
  RecipientChatHeader,
  LocationIndicator,
  ChatStatus,
  ChatNote,
  ChatTextWrap,
  ChatAuthor,
  TranslateWrap,
  TranslatedText,
  ChatToggle,
  ChatMenu,
  ChatWrap,
  ChatMenuItem,
  MessageNote,
  TemplateActionList
} from "@components/chatStyles";
import { ActionsContainer, ItalicSpan, UnderlinedSpan, FlexCenterRow } from "@components/styles";
import { Attachment } from "@components/Attachment";
import Linkify from "react-linkify";
import { connect } from "react-redux";
import { IRootState } from "@utils";
import { formatPhoneNumber, getLastLocationTime, useVisualViewportResize, useVisualViewportContainerResize } from "@lib/utils/helpers";
import {
  setConversationLocation,
  setVideoRoomCount,
  setRemoteRecipientCamSettings,
  setVideoNoShow,
  setActiveRoomData,
  setConversationEntity
} from "@actions";
import { ChatActions } from "./ChatActions";
import { VideoActions } from "./VideoActions";
import { SendMessageCTA } from "./SendMessageCTA";
import { TranscribeActions } from "./TranscribeActions";
import { TemplatePrompt } from "@components/TemplatePrompt";
import { LocationModal } from "@components/LocationModal";
// @ts-ignore
import BulletList from "@assets/icons/bulletList.svg";
import { LanguageSelector } from "@components/Video/LanguageSelector";

interface ISMSChatProps {
  conversationId: string;
  identity: string;
  recipientId: string;
  phoneNumber: string;
  ui?: boolean;
  greetingMessage?: string;
  smsLanguage?: string;
  dialects: IDialectEntity[];
  recipient?: boolean;
  locationRequest?: boolean;
  onSMS: (message: IMessageEntity) => void;
  onSMSEnded: (closed: boolean) => void;
  $element: HTMLElement;
  conversations: IRootState["conversations"];
  agency: IRootState["agency"];
  app: IRootState["app"];
  setConversationLocation: typeof setConversationLocation;
  setVideoRoomCount: typeof setVideoRoomCount;
  setRemoteRecipientCamSettings: typeof setRemoteRecipientCamSettings;
  setActiveRoomData: typeof setActiveRoomData;
  setConversationEntity: typeof setConversationEntity;
}

const XSMSChat: FunctionComponent = ({
  conversationId,
  identity,
  dialects,
  recipientId,
  phoneNumber,
  locationRequest,
  onSMS,
  onSMSEnded,
  ui,
  recipient,
  $element,
  conversations,
  app,
  agency,
  setConversationLocation,
  setVideoRoomCount,
  setRemoteRecipientCamSettings,
  setActiveRoomData,
  setConversationEntity
}: ISMSChatProps) => {
  const visualViewport = useVisualViewportResize();
  useVisualViewportContainerResize($element, visualViewport);
  const { agencyId, agencyName } = app;
  const currentConversation = conversations[conversationId];
  // A recipient can only chat while the session is active: once it is closed
  // (or archived) the input is hidden. An unknown status (still loading) keeps it.
  const conversationStatus = currentConversation?.entity?.status;
  const recipientChatClosed = !!recipient && !!conversationStatus && conversationStatus !== "active";
  const [active, setActive] = useState(true);
  const [ttsActive, setTTSActive] = useState(currentConversation?.entity?.channel !== "voice" ? false : true);
  const [templateShow, setTemplateShow] = useState(false);
  const [nextNavShown, setNextNavShown] = useState(false);
  const [showMap, setShowMap] = useState(0);
  const [text, setText] = useState("");
  const [, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [locationIsFresh, setLocationIsFresh] = useState(false);
  const [locationSharing, setLocationSharing] = useState<boolean | null>(null);
  const [templates, setTemplates] = useState<ITemplateEntity[]>([]);
  const intervalIDRef = useRef(null);
  const watchIDRef = useRef<number | null>(null);
  const conversationIDRef = useRef(conversationId);
  const recipientIDRef = useRef(recipientId);
  const langRef = useRef("");
  const dialectRef = useRef("");

  useEffect(() => {
    (async () => {
      const messagesResponse = await ConveyApi.getMessages({ conversationId });
      const templatesResponse = await ConveyApi.getTemplates();
      const uniqueTemplates = templatesResponse.filter((template, templateIndex, self) => {
        return templateIndex === self.findIndex((t) => t.template_id === template.template_id);
      });
      setTemplates(uniqueTemplates);
      const filteredMessages = messagesResponse.filter((message) => message.source !== "transcribe");
      messagesRef.current = [...filteredMessages, ...messagesRef.current];
      setMessages([...messagesRef.current]);
      scrollBottom();
    })();
  }, []);
  const messagesRef = useRef([]);
  const [, setMessages] = useState<IMessageEntity[]>([]);
  useEffect(() => {
    ConveySocket.init(ConveyApi.readAccessToken())
      .listen(
        ConveySocket.UPDATE_CONVERSATION,
        (data) => {
          setConversationEntity(data.conversation_id, data);
        },
        true
      )
      .listen(
        ConveySocket.CLOSE_CONVERSATION,
        (data) => {
          if (data.conversation_id === conversationIDRef.current) {
            setConversationEntity(data.conversation_id, { ...data, status: "closed" });
          }
        },
        true
      )
      .listen(
        ConveySocket.CREATE_ATTACHMENT,
        (data) => {
          if (data.conversation_id === conversationIDRef.current && data.presigned_url) {
            const msgIndex = messagesRef.current.findIndex((m) => m.message_id === data.message_id);
            if (msgIndex !== -1) {
              messagesRef.current[msgIndex] = {
                ...messagesRef.current[msgIndex],
                image_url: data.presigned_url,
                pending_image: false
              };
              setMessages([...messagesRef.current]);
              scrollBottom();
            }
          }
        },
        true
      )
      .listen(
        ConveySocket.CREATE_MESSAGE,
        (data) => {
          if (data.conversation_id === conversationIDRef.current && data.source !== "transcribe") {
            const messageData = {
              ...data
            };
            if (data.num_media > 0) {
              messageData.pending_image = true;
            }
            messagesRef.current = [...messagesRef.current, ...[messageData]];
            setMessages([...messagesRef.current]);
            // Announce inbound messages for screen readers
            if (data.direction === "inbound") {
              const sender = data.recipient_label || "Dispatcher";
              announcePolite(`New message from ${sender}`);
            }
            scrollBottom();
          }
          onSMS && onSMS(data);
        },
        true
      )
      .listen(
        ConveySocket.UPDATE_MESSAGE,
        (data) => {
          // The translation of a message (body_transl / language) is written
          // after the message is created and only reaches the chat through
          // this update. Without merging it, a non-English recipient keeps
          // seeing the agent's original English text.
          if (!data?.message_id || data.conversation_id !== conversationIDRef.current) return;
          const msgIndex = messagesRef.current.findIndex((m) => m.message_id === data.message_id);
          if (msgIndex === -1) return;
          const changes = Object.keys(data).reduce((acc, key) => {
            if (data[key] !== undefined && data[key] !== null) acc[key] = data[key];
            return acc;
          }, {} as Record<string, unknown>);
          messagesRef.current[msgIndex] = { ...messagesRef.current[msgIndex], ...changes };
          setMessages([...messagesRef.current]);
        },
        true
      )
      .listen(
        ConveySocket.UPDATE_RECIPIENT_LANGUAGE,
        (data) => {
          if (data.recipient_id === recipientIDRef.current) {
            const newLang =
              app.textDialects.find((d) => {
                return d.code === data?.language;
              })?.label || "";
            const newDialectCode = app.textDialects.find((d) => d.code === data?.language)?.code || "";
            langRef.current = newLang;
            dialectRef.current = newDialectCode;
            setLang(newLang);
            setDialectCode(newDialectCode);
          }
        },
        true
      )
      .listen(
        ConveySocket.UPDATE_MESSAGE_STATUS,
        (data) => {
          if (data.conversation_id === conversationIDRef.current) {
            const $statusElement = document.getElementById(`chat-status-id-${data.message_id}`);
            if ($statusElement) {
              $statusElement.innerHTML = data.status;
            }
          }
          onSMS && onSMS(data);
        },
        true
      )
      .listen(
        ConveySocket.ADD_RECIPIENT_GEOLOCATION,
        (data) => {
          if (data.recipient_id === recipientIDRef.current) {
            onSMS && onSMS(data);
            const {
              lng,
              lat,
              normalized_address,
              updated_at,
              latlng_accuracy,
              altitude,
              altitude_accuracy,
              hat,
              hat_accuracy
            } = data;
            setConversationLocation(conversationIDRef.current, {
              lng,
              lat,
              hat,
              altitude,
              normalizedAddress: normalized_address,
              updatedAt: updated_at,
              latlngAccuracy: latlng_accuracy,
              altitudeAccuracy: altitude_accuracy,
              hatAccuracy: hat_accuracy
            });
          }
        },
        true
      )
      .listen(
        ConveySocket.RECEIVE_SHARE_INFORMATION,
        (data) => {
          if (data.type === "share-video-settings") {
            setRemoteRecipientCamSettings({
              identity: data.data.identity,
              settings: data.data.mVideoSettings
            });
          }
        },
        true
      )
      .listen(
        ConveySocket.CREATE_VIDEO_ROOM,
        (data) => {
          getRoom();
        },
        true
      )
      .listen(
        ConveySocket.CLOSE_VIDEO_ROOM,
        () => {
          getRoom();
        },
        true
      );
  }, []);

  useEffect(() => {
    if (currentConversation?.location?.updatedAt) {
      setLocationIsFresh(
        currentConversation.location?.updatedAt &&
          currentConversation.location?.updatedAt / 1000 > Math.floor(Date.now() / 1000 - 15 * 60)
      );
      if (!intervalIDRef.current) {
        intervalIDRef.current = setInterval(() => {
          setLocationIsFresh(
            currentConversation.location?.updatedAt &&
              currentConversation.location?.updatedAt / 1000 > Math.floor(Date.now() / 1000 - 15 * 60)
          );
        }, 30 * 1000);
      }

      return () => {
        if (intervalIDRef.current) {
          clearInterval(intervalIDRef.current);
        }
      };
    }
  }, [currentConversation?.location?.updatedAt]);

  const createMessage = useCallback(async () => {
    if (recipientChatClosed) return;
    setSending(true);
    const response = await ConveyApi.createMessage({
      body: text,
      conversationId,
      phoneNumber,
      recipient,
      ttsActive
    });
    setText("");
    setSending(false);
    if (response && !recipient) {
      scrollBottom();
    }
  }, [ttsActive, text, conversationId, phoneNumber, recipient, recipientChatClosed]);
  const scrollBottom = () => {
    setTimeout(() => {
      const wrapperEle = document.getElementById("sms-container");
      if (wrapperEle) {
        wrapperEle.scrollTo(0, 100000);
      }
    }, 250);
  };
  useEffect(() => {
    if (visualViewport) {
      scrollBottom();
    }
  }, [visualViewport]);

  const dialectLabel = (code: string) => {
    let label = "";
    try {
      label = dialects.find((o) => o.code === code).label;
    } catch (error) {
      return code;
    }
    return label;
  };

  // Label for the secondary text under a translated message. `language` is
  // always the recipient's language, so the wording depends on who is reading:
  // - agent: the main text is the agent's, the secondary is the translation
  //   into / from the recipient's language;
  // - recipient: the main text is in their own language, so under the agent's
  //   messages the secondary is the agent's original, and under their own
  //   messages it is what the agency received.
  const translationNote = (direction: string, language: string) => {
    if (recipient) {
      if (direction === "inbound") return "Original message";
      return agencyName ? `Translated for ${agencyName}` : "Translated";
    }
    return `Translated ${direction === "outbound" ? "into" : "from"} ${dialectLabel(language)}`;
  };

  const postLocation = async (coords: GeolocationCoordinates) => {
    await ConveyApi.postRecipientGeoLocations({
      recipientId,
      lat: coords.latitude,
      lng: coords.longitude,
      latlng_accuracy: coords.accuracy,
      altitude: coords.altitude,
      altitude_accuracy: coords.altitudeAccuracy
    });
  };

  const geoSuccessHandler = (position: GeolocationPosition) => {
    postLocation(position.coords);
    setLocationSharing(true);
  };

  const geoErrorHandler = (error: GeolocationPositionError) => {
    console.log(error);
    setLocationSharing(false);
    if (watchIDRef.current !== null) {
      navigator.geolocation.clearWatch(watchIDRef.current);
      watchIDRef.current = null;
    }
    setError("Location settings are not enabled in your browser. Please enter your address below.");
  };

  const requestLocation = () => {
    if (navigator.geolocation) {
      if (watchIDRef.current !== null) {
        navigator.geolocation.clearWatch(watchIDRef.current);
        watchIDRef.current = null;
      }
      navigator.geolocation.getCurrentPosition(geoSuccessHandler, geoErrorHandler);
      watchIDRef.current = navigator.geolocation.watchPosition(geoSuccessHandler, geoErrorHandler);
    } else {
      setLocationSharing(false);
      setError("Your browser does not support geolocation API. Please enter your address manually.");
    }
  };

  useEffect(() => {
    if (recipient && locationRequest) {
      requestLocation();
    }
  }, [recipient, locationRequest]);

  useEffect(() => {
    (async () => {
      const currentRooms = await ConveyApi.getVideoRooms({
        conversationId: conversationId,
        status: "active"
      });
      if (currentRooms.length > 0) {
        setActiveRoomData(currentRooms[0]);
      }
      setVideoRoomCount(currentRooms.length);
    })();
  }, [conversationId]);

  const [lang, setLang] = useState("-- Autodetect Language --");
  const [dialectCode, setDialectCode] = useState("");

  useEffect(() => {
    if (currentConversation?.entity?.recipients && currentConversation?.entity?.recipients[0]?.language) {
      const newLang =
        app.textDialects.find((d) => d.code === currentConversation?.entity?.recipients[0]?.language)?.label || "";
      const newDialectCode =
        app.textDialects.find((d) => d.code === currentConversation?.entity?.recipients[0]?.language)?.code || "";
      langRef.current = newLang;
      dialectRef.current = newDialectCode;
      setLang(newLang);
      setDialectCode(newDialectCode);
    }
  }, [currentConversation?.entity]);

  const getRoom = async () => {
    (async () => {
      const currentRooms = await ConveyApi.getVideoRooms({
        conversationId: conversationId,
        status: "active"
      });
      if (currentRooms.length > 0) {
        setActiveRoomData(currentRooms[0]);
      } else {
        setActiveRoomData(null);
        app.videoShow && setVideoNoShow();
      }
      setVideoRoomCount(currentRooms.length);
    })();
  };

  const canSendMessage =
    agency?.settings?.enableConversationAssignment === false ||
    recipient ||
    currentConversation.entity.operator_id === null ||
    currentConversation.entity.operator_id === app.operatorId;

  const putRecipientLanguage = async (language: string) => {
    await ConveyApi.putRecipientLanguage({
      language,
      recipientId
    });
    ConveySocket.getInstance().socketEmit(ConveySocket.SHARE_INFORMATION_CMD, {
      conversationId,
      type: "recognition-language",
      data: { [recipientId]: language }
    });
  };

  return (
    active &&
    ui !== false && (
      <ChatWrap>
        <style>{`
          @keyframes locationPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
        `}</style>
        <ChatWrapContainer>
          {!recipient && (
            <ActionsContainer>
              <FlexCenterRow justify="space-between" style={{ width: "100%" }}>
                {currentConversation?.entity?.primary_recipient && (
                  <div>
                    <>
                      <span data-type="Conversation-Label">
                        {formatPhoneNumber(currentConversation?.entity?.primary_recipient)}
                      </span>
                      {dialectRef.current && (
                        <LanguageSelector
                          onSelect={(selectedLanguage) => {
                            setLang(selectedLanguage.label);
                            putRecipientLanguage(selectedLanguage.code);
                          }}
                          dialects={app.textDialects}
                          currentDialect={dialectRef.current}
                          asText={true}
                        />
                      )}
                    </>
                  </div>
                )}
                <ChatActions
                  conversationId={conversationId}
                  agencyId={agencyId}
                  phoneNumber={phoneNumber}
                  recipientId={recipientId}
                  onSMSClose={() => {
                    setActive(false);
                    onSMSEnded && onSMSEnded(true);
                  }}
                />
              </FlexCenterRow>
              {locationIsFresh && currentConversation?.location && (
                <div style={{ width: "100%" }}>
                  <MessageNote
                    data-type="Conversation-Location"
                    style={{ paddingLeft: 0 }}
                    onClick={() => {
                      setShowMap(new Date().getTime());
                    }}
                  >
                    {<UnderlinedSpan>{currentConversation.location.normalizedAddress}</UnderlinedSpan>}
                    {currentConversation.location.updatedAt && (
                      <ItalicSpan>&nbsp;{getLastLocationTime(currentConversation.location.updatedAt)}</ItalicSpan>
                    )}
                  </MessageNote>
                  {!!showMap && (
                    <LocationModal
                      $mapId={"LocationHeader"}
                      show={showMap}
                      nextNavShown={nextNavShown}
                      conversationId={conversationId}
                      onClose={() => {
                        setShowMap(0);
                      }}
                      mapLoaded={() => {
                        setNextNavShown(true);
                      }}
                      locationIsFresh={locationIsFresh}
                    />
                  )}
                </div>
              )}
            </ActionsContainer>
          )}
          {!recipient && (
            <>
              <VideoActions
                conversationId={conversationId}
                agencyId={agencyId}
                phoneNumber={phoneNumber}
                recipientId={recipientId}
              />
              <TranscribeActions conversationId={conversationId} agencyId={agencyId} phoneNumber={phoneNumber} />
            </>
          )}
          {recipient && (
            <RecipientChatHeader
              status={
                (currentConversation?.entity?.status === "closed" || currentConversation?.entity?.status === "archived")
                  ? "closed"
                  : currentConversation?.entity?.operator_id
                  ? "assigned"
                  : "waiting"
              }
            >
              <span>
                {currentConversation?.entity?.status === "closed" || currentConversation?.entity?.status === "archived"
                  ? "Session Closed"
                  : currentConversation?.entity?.operator_id
                  ? "Agent assigned"
                  : "Waiting for agent"}
              </span>
              {locationSharing !== null && currentConversation?.entity?.status === "active" && (
                locationSharing ? (
                  <LocationIndicator active={true} style={{ animation: "locationPulse 2s ease-in-out infinite" }}>
                    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" />
                    </svg>
                    Location sharing active
                  </LocationIndicator>
                ) : (
                  <LocationIndicator
                    active={false}
                    onClick={requestLocation}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        requestLocation();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Location sharing disabled, click to re-enable"
                    style={{ cursor: "pointer", color: "#dc2626", fontWeight: 700 }}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" />
                    </svg>
                    Location sharing disabled
                  </LocationIndicator>
                )
              )}
            </RecipientChatHeader>
          )}
          <ChatContainer id="sms-container" recipientView={recipient}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start"
              }}
            >
              {messagesRef.current.map((message, messageIndex) => {
                const realDirection = recipient
                  ? message.direction === "inbound"
                    ? "outbound"
                    : "inbound"
                  : message.direction;
                const realIdentity = recipient
                  ? realDirection === "outbound"
                    ? identity
                    : agencyName
                  : realDirection === "inbound"
                  ? message.recipient_label
                  : identity;
                const isNote = message.source === "note";
                const isTTS = message.source === "voice";
                const messageDirection = isNote ? "note" : realDirection;
                // const locationRequestMsg = message.body?.includes("share location");
                const bodyText =
                  realDirection === "outbound"
                    ? message.body
                    : message.body_transl
                    ? message.body_transl
                    : message.body;
                const transBodyText =
                  realDirection === "outbound"
                    ? message.body_transl
                      ? message.body_transl
                      : message.body
                    : message.body;
                return message.pending_image ? null : (
                  <ChatTextWrap
                    key={[message.message_id, messageIndex].join()}
                    direction={messageDirection}
                    hide={isNote && recipient}
                  >
                    {isNote ? (
                      !recipient && <ChatNote>{message.body}</ChatNote>
                    ) : (
                      <>
                        <ChatAuthor direction={realDirection}>{formatPhoneNumber(realIdentity)}</ChatAuthor>
                        <ChatText direction={realDirection}>
                          <Linkify>{bodyText}</Linkify>
                          {/* {realDirection === "outbound" ? bodyText : <Linkify>{bodyText}</Linkify>} */}
                          {message.body &&
                            message.body_transl &&
                            message.body !== message.body_transl &&
                            message.language &&
                            message.language.substring(0, 2) != "en" && (
                              <TranslateWrap>
                                {
                                  <>
                                    {message.language && (
                                      <ChatNote emphasized={true}>
                                        {translationNote(realDirection, message.language)}
                                      </ChatNote>
                                    )}
                                    <TranslatedText>
                                      <Linkify>{transBodyText}</Linkify>
                                    </TranslatedText>
                                  </>
                                }
                              </TranslateWrap>
                            )}
                          <Attachment
                            messageId={message.message_id}
                            conversationId={conversationId}
                            numMedia={message.num_media}
                            imageUrl={message.image_url}
                          />
                        </ChatText>
                        {/* {locationRequestMsg && (
                          <LocationModule
                            locationIsFresh={locationIsFresh}
                            conversationId={conversationId}
                            direction={realDirection}
                            nextNavShown={nextNavShown}
                            mapLoaded={() => {
                              setNextNavShown(true);
                            }}
                            messageId={[message.message_id, messageIndex].join("")}
                          />
                        )} */}
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {isTTS && (
                            <ChatStatus id={`chat-tts-status-id-${message.message_id}`} direction={realDirection}>
                              Sent as speech
                            </ChatStatus>
                          )}
                          {currentConversation?.entity?.channel !== "voice" && (
                            <ChatStatus id={`chat-status-id-${message.message_id}`} direction={realDirection}>
                              {message.status === "pending" ? "queued" : message.status}
                            </ChatStatus>
                          )}
                        </div>
                      </>
                    )}
                  </ChatTextWrap>
                );
              })}
            </div>
          </ChatContainer>
          {!recipientChatClosed && (
            <ChatInputContainer>
              <ChatInput
                recipient={recipient || !canSendMessage}
                aria-label="Type a message"
                onChange={(e) => {
                  setText(e.target.value);
                }}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.keyCode === 13) && !e.shiftKey) {
                    e.preventDefault();
                    createMessage();
                  }
                }}
                value={text}
                placeholder={
                  !recipient && !canSendMessage
                    ? "You must be assigned as agent on the conversation in order to reply"
                    : ""
                }
                disabled={sending || (!recipient && !canSendMessage)}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error: enterKeyHint is valid on textarea per HTML spec; missing from @types/react TextareaHTMLAttributes
                enterKeyHint="send"
                inputMode="text"
                autoCapitalize="sentences"
                autoCorrect="on"
                spellCheck={true}
              />
              {!recipient && canSendMessage && (
                <ChatMenu>
                  <ChatToggle
                    type="button"
                    aria-label="Toggle template menu"
                    onClick={() => {
                      setTemplateShow(!templateShow);
                    }}
                  >
                    <BulletList aria-hidden="true" />
                  </ChatToggle>
                  <TemplateActionList show={templateShow}>
                    {templates.map((template, index) => (
                      <ChatMenuItem key={index}>
                        <TemplatePrompt
                          template={template}
                          conversationId={conversationId}
                          phoneNumber={phoneNumber}
                          recipient={recipient}
                        />
                      </ChatMenuItem>
                    ))}
                  </TemplateActionList>
                </ChatMenu>
              )}
              <SendMessageCTA
                ttsActive={ttsActive}
                isLoading={sending}
                isTTSEnabled={currentConversation?.entity?.channel === "voice"}
                disabled={sending || (!recipient && !canSendMessage)}
                onSendMessage={() => createMessage()}
                onChangeTTSActive={(value) => {
                  setTTSActive(value);
                }}
              />
            </ChatInputContainer>
          )}
        </ChatWrapContainer>
      </ChatWrap>
    )
  );
};

const mapStateToProps = ({ conversations, app, agency }: IRootState) => ({
  conversations,
  app,
  agency
});

export const SMSChat = connect(mapStateToProps, {
  setConversationLocation,
  setVideoRoomCount,
  setRemoteRecipientCamSettings,
  setActiveRoomData,
  setConversationEntity
})(XSMSChat);
